import { useState, useEffect, useCallback } from 'react';
import { supabase } from '~/services/supabase';

export interface IIntegrationStatus {
  id: string;
  provider: string;
  provider_config: Record<string, unknown>;
  scopes: string[] | null;
  connected_by: string;
  created_at: string;
  updated_at: string;
}

interface UseIntegrationResult {
  connected: boolean;
  integration: IIntegrationStatus | null;
  loading: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  handleCallback: (code: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useIntegration = (provider: string): UseIntegrationResult => {
  const [integration, setIntegration] = useState<IIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(`${provider}-oauth`, {
        body: { action: 'status' },
      });

      if (fnError) throw fnError;
      setIntegration(data.connected ? data.integration : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    fetchStatus();

    const channel = supabase
      .channel(`integration-${provider}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'integrations', filter: `provider=eq.${provider}` },
        () => fetchStatus(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchStatus, provider]);

  const connect = useCallback(async () => {
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(`${provider}-oauth`, {
        body: { action: 'initiate' },
      });

      if (fnError) throw fnError;
      if (data.authUrl) {
        if (data.state) {
          sessionStorage.setItem(`oauth_state_${provider}`, data.state);
        }
        window.location.href = data.authUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate connection');
    }
  }, [provider]);

  const disconnect = useCallback(async () => {
    setError(null);
    try {
      const { error: fnError } = await supabase.functions.invoke(`${provider}-oauth`, {
        body: { action: 'disconnect' },
      });

      if (fnError) throw fnError;
      setIntegration(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    }
  }, [provider]);

  const handleCallback = useCallback(
    async (code: string) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fnError } = await supabase.functions.invoke(`${provider}-oauth`, {
          body: { action: 'callback', code },
        });

        if (fnError) throw fnError;
        if (!data.success) {
          throw new Error(data.error ?? 'OAuth callback failed');
        }

        // Refresh status after successful callback
        await fetchStatus();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'OAuth callback failed');
        setLoading(false);
      }
    },
    [provider, fetchStatus],
  );

  return {
    connected: !!integration,
    integration,
    loading,
    error,
    connect,
    disconnect,
    handleCallback,
    refresh: fetchStatus,
  };
};
